import { useMemo, useState } from 'react';
import { resolveWeightId, weightById } from '../../domain/catalogs';
import { searchYarns } from '../../domain/yarnCatalog';
import { X } from '../../components/icons';
import type { EditorApi } from './useEditor';

/** Search the bundled catalogue and link a real yarn to the active colour. */
export function YarnLink({ editor }: { editor: EditorApi }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hot, setHot] = useState(0);
  const results = useMemo(() => searchYarns(q, 8), [q]);
  const linked = editor.links[editor.activeYarn];

  const pick = (y: ReturnType<typeof searchYarns>[number]) => {
    editor.setLink(editor.activeYarn, {
      brand: y.brand,
      line: y.line,
      weightId: y.weightId,
      fiber: y.fiber,
      fiberClass: y.fiberClass,
      colorway: null,
    });
    const wid = resolveWeightId(y.weightId);
    if (wid && weightById(wid)) editor.setGauge(wid);
    setQ('');
    setOpen(false);
  };

  return (
    <div className="relative mb-3">
      <div className="flex gap-1.5">
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            setHot(0);
          }}
          onFocus={() => q.length >= 2 && setOpen(true)}
          onKeyDown={(e) => {
            if (!open || !results.length) return;
            if (e.key === 'ArrowDown') {
              setHot((h) => Math.min(results.length - 1, h + 1));
              e.preventDefault();
            } else if (e.key === 'ArrowUp') {
              setHot((h) => Math.max(0, h - 1));
              e.preventDefault();
            } else if (e.key === 'Enter') {
              pick(results[hot]);
              e.preventDefault();
            }
          }}
          placeholder="Link a real yarn… (e.g. Cascade 220)"
          className="h-9 flex-1 rounded-xl border border-border bg-surface-2 px-3 text-sm outline-none focus-visible:border-accent"
        />
        {linked && (
          <button
            title="Unlink yarn"
            aria-label="Unlink yarn"
            onClick={() => editor.clearLink(editor.activeYarn)}
            className="grid h-9 w-9 place-items-center rounded-xl border border-border text-text-3 hover:text-danger"
          >
            <X width={14} height={14} />
          </button>
        )}
      </div>
      {linked && (
        <div className="mt-1 truncate text-xs text-accent">
          {linked.brand} {linked.line}
          {linked.colorway ? ` · ${linked.colorway}` : ''} · {linked.fiber}
        </div>
      )}
      {open && q.length >= 2 && (
        <div className="absolute inset-x-0 top-full z-30 mt-1 max-h-64 overflow-auto rounded-xl border border-border bg-elevated p-1" style={{ boxShadow: 'var(--shadow)' }}>
          {results.length === 0 ? (
            <div className="p-2.5 text-xs italic text-text-3">No matching yarn — type a brand or line</div>
          ) : (
            results.map((y, i) => {
              const w = weightById(resolveWeightId(y.weightId) ?? '');
              return (
                <button
                  key={`${y.brand}-${y.line}`}
                  onMouseEnter={() => setHot(i)}
                  onClick={() => pick(y)}
                  className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left ${i === hot ? 'bg-surface-2' : ''}`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">
                      {y.brand} {y.line}
                    </span>
                    <span className="block truncate text-xs text-text-3">{y.fiber}</span>
                  </span>
                  <span className="flex-none font-mono text-[10px] text-accent">{w ? w.name : y.weightId}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
